import type { ChangeEvent, ReactElement } from 'react';
import classNames from 'classnames';
import type { SortOption } from '@/interfaces';

export interface SortDropdownStateProps {
    id: string;
    selectedValue: SortOption;
    sortOptions: EnrichedSortOption[];
    horizontal?: boolean;
    labelClassName?: string;
}

export interface SortDropdownDispatchProps {
    filterChange: (newSortValue?: SortOption) => void;
    triggerFetch?: (resetMap: boolean) => void;
}

type EnrichedSortOption = SortOption & { displayValue: string };

export type SortDropdownProps = SortDropdownStateProps & SortDropdownDispatchProps;

export const SortDropdown = ({
    id,
    selectedValue,
    sortOptions,
    filterChange,
    triggerFetch,
    horizontal = false,
    labelClassName = '',
}: SortDropdownProps) => {
    const concatValues = (sortOption: SortOption | EnrichedSortOption): string => `${sortOption.value}|${sortOption.direction}`;

    const splitValues = (sortOption: string): SortOption | undefined => {
        const parts = sortOption.split('|');
        if (parts[0] === 'undefined' || parts[1] === 'undefined') {
            return undefined;
        }
        return { value: parts[0], direction: parts[1] };
    };

    const onChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        filterChange(splitValues(event.target.value));
        triggerFetch?.(false);
    };

    return (
        <div className={classNames('input u-mb0 input--select', { 'horizontal-sort-dropdown': horizontal })}>
            <label className={classNames(labelClassName, { 'u-r-size1of4': horizontal })} htmlFor={id}>
                Sorter på
            </label>
            <div className={classNames('input--select__wrap', { 'u-r-size3of4': horizontal })}>
                <select
                    data-testid="sortDropdown"
                    id={id}
                    size={1}
                    value={concatValues(selectedValue)}
                    onChange={onChange}
                    onBlur={(): undefined => undefined}>
                    {sortOptions.reduce((components: ReactElement[], option: EnrichedSortOption): ReactElement[] => {
                        const component = (
                            <option key={concatValues(option)} value={concatValues(option)}>
                                {option.displayValue}
                            </option>
                        );
                        components.push(component);

                        return components;
                    }, [])}
                </select>
            </div>
        </div>
    );
};
